import { common, grey } from "@mui/material/colors";
import {
  createTheme,
  ThemeOptions,
  SimplePaletteColorOptions,
  alpha,
} from "@mui/material/styles";
import { SansRegularFont } from "./fonts";

interface TextColors {
  primary: string;
  secondary: string | undefined;
  disabled: string;
  hint: string;
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    breadcrumb: true;
    "back-button": true;
    "reverse-contained": true;
    "remove-button": true;
    "green-contained": true;
    "green-outlined": true;
    "text-icon": true;
    "small-italic": true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    breadcrumb: true;
    "theme-title": true;
    "panel-title": true;
    "graph-label": true;
    "h1-no-bold": true;
    "h3-black": true;
    "h4-green": true;
    "small-italic": true;
  }
}

export const theme = (() => {
  const sansFont =
    "FlandersSans ,-apple-system, BlinkMacSystemFont, avenir next, avenir, segoe ui, helvetica neue, helvetica, Ubuntu, roboto, noto, arial, sans-serif;";

  const error: SimplePaletteColorOptions = {
    main: "#C63131",
  };

  const primary: SimplePaletteColorOptions = {
    main: "#227799",
  };

  const defaultTheme = createTheme({
    palette: { error, primary },
    typography: {
      htmlFontSize: 19.75,
      fontFamily: sansFont,
    },
  });

  const borderRadius: number = 0;

  const secondary: SimplePaletteColorOptions = {
    main: "#26b8eb",
    contrastText: defaultTheme.palette.common.white,
  };
  const success: SimplePaletteColorOptions = {
    main: "#84ac3f",
    dark: "#6c981f",
    light: "#84ac3f",
    contrastText: defaultTheme.palette.common.black,
  };

  const warning: SimplePaletteColorOptions = {
    light: "rgb(183 ,171 ,31)",
    main: "rgb(150 ,140 ,25)",
    dark: "rgb(108 ,101 ,18)",
    contrastText: defaultTheme.palette.common.white,
  };
  const text: TextColors = {
    primary: "rgba(0, 0, 0, 1)",
    secondary: "rgba(0, 0, 0, 0.8)",
    disabled: "rgba(0, 0, 0, 0.4)",
    hint: "rgba(0, 0, 0, 0.4)",
  };

  const overrides: ThemeOptions = {
    palette: {
      secondary,
      text,
      success,
      warning,
    },
    typography: {
      fontFamily: sansFont,
      h1: {
        fontSize: "28px",
        fontWeight: 600,
        color: primary.main,
      },
      h2: {
        fontSize: "18px",
        fontWeight: 600,
        color: primary.main,
      },
      h3: {
        fontSize: "16px",
        fontWeight: 600,
        color: primary.main,
      },
      h4: {
        fontSize: "13px",
        fontWeight: 600,
        color: primary.main,
      },

      subtitle1: {
        fontSize: "13px",
        fontWeight: 600,
        color: common.black,
      },
      subtitle2: {
        fontSize: "13px",
        fontWeight: "normal",
        color: primary.main,
      },
      body2: {
        fontWeight: 700,
        fontSize: "13px",
      },
    },

    shape: {
      borderRadius: borderRadius,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@font-face": SansRegularFont,
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            margin: 0,
            fontSize: "13px",
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: common.white,
          },
          primary: {
            backgroundColor: primary.main,
            color: common.white,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontWeight: 400,
          },
        },
      },

      MuiTypography: {
        variants: [
          {
            props: { variant: "h1-no-bold" },
            style: {
              color: primary.main,
              fontWeight: 400,
              fontSize: "28px",
            },
          },
          {
            props: { variant: "h3-black" },
            style: {
              fontSize: "16px",
              fontWeight: 600,
            },
          },
          {
            props: { variant: "h4-green" },
            style: {
              fontSize: "13px",
              fontWeight: 600,
              color: success.main,
            },
          },
          {
            props: { variant: "small-italic" },
            style: {
              fontSize: "12px",
              color: defaultTheme.palette.grey[600],
              fontStyle: "italic",
            },
          },
          {
            props: { variant: "breadcrumb" },
            style: {
              color: defaultTheme.palette.grey[900],
            },
          },
          {
            props: { variant: "theme-title" },
            style: {
              fontWeight: 700,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              "&>.MuiAvatar-root": {
                marginRight: defaultTheme.spacing(1),
              },
            },
          },
          {
            props: { variant: "graph-label" },
            style: {
              fontWeight: 700,
              fontSize: "18px",
              textTransform: "uppercase",
            },
          },
          {
            props: { variant: "caption" },
            style: {
              fontWeight: 300,
              color: defaultTheme.palette.grey[500],
              textTransform: "uppercase",
              fontSize: "0.7rem",
              margin: "0",
            },
          },
          {
            props: { variant: "subtitle2" },
            style: {
              fontSize: "0.9rem",
              color: primary.main,
            },
          },
          {
            props: { variant: "panel-title" },
            style: {
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "uppercase",
              color: defaultTheme.palette.grey[900],
            },
          },
          {
            props: { variant: "small-italic" },
            style: {
              fontSize: "11px",
              textDecoration: "italic",
              color: defaultTheme.palette.grey[600],
            },
          },
        ],
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            color: "inherit",
            marginTop: 0,
            fontSize: "14px",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            alignItems: "flex-start",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
        variants: [
          {
            props: { variant: "outlined" },
            style: {},
          },
        ],
      },

      MuiButton: {
        styleOverrides: {
          outlined: {},
        },
        variants: [
          {
            props: { variant: "breadcrumb" },
            style: {
              textTransform: "inherit",
              fontWeight: 700,
              fontSize: "1rem",
              color: primary.main,
            },
          },
          {
            props: { variant: "text-icon" },
            style: {
              textTransform: "none",
              fontSize: "13px",
              justifyContent: "flex-start",
              marginLeft: defaultTheme.spacing(-1),
              fontWeight: 600,
              "&.Mui-disabled": {
                ".MuiButton-startIcon>*:nth-of-type(1)": {
                  color: defaultTheme.palette.text.disabled,
                  fontSize: defaultTheme.spacing(4),
                },
              },
              ".MuiButton-startIcon>*:nth-of-type(1)": {
                color: primary.main,
                fontSize: defaultTheme.spacing(4),
              },
            },
          },
          {
            props: { variant: "back-button" },
            style: {
              fontSize: "13px",
              textTransform: "none",
              color: primary.main,
              fontWeight: 600,
            },
          },
          {
            props: { variant: "contained" },
            style: {
              fontWeight: 700,
              fontSize: "0.813rem",
            },
          },
          {
            props: {
              variant: "green-contained",
            },
            style: {
              backgroundColor: success.main,
              color: common.white,
              fontWeight: 700,
              fontSize: "0.813rem",
              ":disabled": {
                backgroundColor: grey[300],
              },
              "&:hover": {
                backgroundColor: success.dark,
              },
            },
          },
          {
            props: {
              variant: "green-outlined",
            },
            style: {
              color: success.main,
              fontWeight: 700,
              fontSize: "0.813rem",
              border: `1px solid ${success.main}`,
              ":disabled": {
                backgroundColor: grey[300],
              },
              "&:hover": {
                backgroundColor: alpha(
                  success.light ? success.light : "green",
                  0.08
                ),
              },
            },
          },
          {
            props: { variant: "remove-button" },
            style: {
              backgroundColor: defaultTheme.palette.error.main,
              color: defaultTheme.palette.common.white,
              fontWeight: 700,
              fontSize: "0.813rem",
              "&:hover": {
                backgroundColor: defaultTheme.palette.error.dark,
              },
            },
          },
          {
            props: { variant: "reverse-contained" },
            style: {
              color: success.main,
              fontWeight: 700,
              fontSize: "0.813rem",
              "&:hover": {
                color: success.dark,
              },
            },
          },
        ],
      },
      MuiLink: {
        variants: [
          {
            props: { variant: "breadcrumb" },
            style: {
              "&.active": {
                textDecoration: "none",
                fontWeight: 700,
                color: primary.main,
              },
              textDecoration: "none",
              fontWeight: "inherit",
              color: defaultTheme.palette.grey[900],
              "&:hover": {
                textDecoration: "underline",
              },
            },
          },
        ],
      },
    },
  };

  return createTheme(defaultTheme, overrides);
})();
